import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';

const API = process.env.REACT_APP_BACKEND_URL;

const emptyReference = {
  title: '',
  property_type: '',
  transaction_type: '',
  location: '',
  zip_code: '',
  price: '',
  price_on_request: false,
  area_sqm: '',
  rooms: '',
  floor: '',
  description: '',
  features: [],
  images: [],
  sold_date: '',
  days_on_market: '',
  testimonial: '',
  testimonial_author: ''
};

export default function AdminReferences() {
  const [references, setReferences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [currentRef, setCurrentRef] = useState(null);
  const [formData, setFormData] = useState(emptyReference);
  const [featuresInput, setFeaturesInput] = useState('');
  const [imagesInput, setImagesInput] = useState('');

  useEffect(() => {
    fetchReferences();
  }, []);

  const fetchReferences = async () => {
    try {
      const response = await axios.get(`${API}/api/references`);
      setReferences(response.data);
    } catch (error) {
      toast.error('Fehler beim Laden der Referenzen');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setCurrentRef(null);
    setFormData(emptyReference);
    setFeaturesInput('');
    setImagesInput('');
    setDialogOpen(true);
  };

  const handleEdit = (ref) => {
    setCurrentRef(ref);
    setFormData({
      ...ref,
      price: ref.price || '',
      rooms: ref.rooms || '',
      floor: ref.floor || '',
      days_on_market: ref.days_on_market || '',
      sold_date: ref.sold_date || '',
      testimonial: ref.testimonial || '',
      testimonial_author: ref.testimonial_author || ''
    });
    setFeaturesInput(ref.features?.join(', ') || '');
    setImagesInput(ref.images?.join('\n') || '');
    setDialogOpen(true);
  };

  const handleDelete = (ref) => {
    setCurrentRef(ref);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/references/${currentRef.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Referenz gelöscht');
      fetchReferences();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
    setDeleteDialogOpen(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    
    const data = {
      ...formData,
      price: formData.price_on_request ? null : parseFloat(formData.price) || null,
      area_sqm: parseFloat(formData.area_sqm),
      rooms: formData.rooms ? parseFloat(formData.rooms) : null,
      days_on_market: formData.days_on_market ? parseInt(formData.days_on_market) : null,
      features: featuresInput.split(',').map(f => f.trim()).filter(Boolean),
      images: imagesInput.split('\n').map(i => i.trim()).filter(Boolean)
    };

    try {
      if (currentRef) {
        await axios.put(`${API}/api/references/${currentRef.id}`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Referenz aktualisiert');
      } else {
        await axios.post(`${API}/api/references`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Referenz erstellt');
      }
      setDialogOpen(false);
      fetchReferences();
    } catch (error) {
      toast.error('Fehler beim Speichern');
    }
  };

  return (
    <div className="p-8" data-testid="admin-references">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-display text-3xl text-white mb-2">Referenzen</h1>
          <p className="text-[#c8c6c4]">{references.length} Einträge</p>
        </div>
        <button onClick={handleCreate} className="btn-primary flex items-center gap-2" data-testid="add-reference-btn">
          <Plus className="w-4 h-4" />
          Neue Referenz
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="spinner" />
        </div>
      ) : references.length === 0 ? (
        <div className="text-center py-20">
          <p className="text-[#c8c6c4]">Noch keine Referenzen vorhanden.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[#d1af6a]/20">
                <th className="text-left py-4 px-4 text-[#d1af6a] font-accent text-xs tracking-wider">Titel</th>
                <th className="text-left py-4 px-4 text-[#d1af6a] font-accent text-xs tracking-wider">Typ</th>
                <th className="text-left py-4 px-4 text-[#d1af6a] font-accent text-xs tracking-wider">Ort</th>
                <th className="text-left py-4 px-4 text-[#d1af6a] font-accent text-xs tracking-wider">Status</th>
                <th className="text-left py-4 px-4 text-[#d1af6a] font-accent text-xs tracking-wider">Tage</th>
                <th className="text-right py-4 px-4 text-[#d1af6a] font-accent text-xs tracking-wider">Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {references.map((ref) => (
                <tr key={ref.id} className="border-b border-[#d1af6a]/10 hover:bg-[#1a2640]">
                  <td className="py-4 px-4 text-white">{ref.title}</td>
                  <td className="py-4 px-4 text-[#c8c6c4]">{ref.property_type}</td>
                  <td className="py-4 px-4 text-[#c8c6c4]">{ref.location}</td>
                  <td className="py-4 px-4">
                    <span className="badge-verkauft px-2 py-1 text-xs">{ref.status}</span>
                  </td>
                  <td className="py-4 px-4 text-[#c8c6c4]">{ref.days_on_market || '-'}</td>
                  <td className="py-4 px-4 text-right">
                    <button onClick={() => handleEdit(ref)} className="p-2 text-[#c8c6c4] hover:text-[#d1af6a]">
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button onClick={() => handleDelete(ref)} className="p-2 text-[#c8c6c4] hover:text-red-400">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#1a2640] border-[#d1af6a]/20 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white font-display text-xl">
              {currentRef ? 'Referenz bearbeiten' : 'Neue Referenz'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <input type="text" placeholder="Titel *" required value={formData.title} onChange={(e) => setFormData(p => ({ ...p, title: e.target.value }))} className="input-luxury w-full" />
            <div className="grid grid-cols-2 gap-4">
              <Select value={formData.property_type} onValueChange={(v) => setFormData(p => ({ ...p, property_type: v }))}>
                <SelectTrigger className="input-luxury"><SelectValue placeholder="Objektart *" /></SelectTrigger>
                <SelectContent className="bg-[#1a2640] border-[#d1af6a]/20">
                  <SelectItem value="Wohnung">Wohnung</SelectItem>
                  <SelectItem value="Haus">Haus</SelectItem>
                  <SelectItem value="Grundstück">Grundstück</SelectItem>
                  <SelectItem value="Gewerbe">Gewerbe</SelectItem>
                </SelectContent>
              </Select>
              <Select value={formData.transaction_type} onValueChange={(v) => setFormData(p => ({ ...p, transaction_type: v }))}>
                <SelectTrigger className="input-luxury"><SelectValue placeholder="Verkauft/Vermietet *" /></SelectTrigger>
                <SelectContent className="bg-[#1a2640] border-[#d1af6a]/20">
                  <SelectItem value="Kaufen">Verkauft</SelectItem>
                  <SelectItem value="Mieten">Vermietet</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <input type="text" placeholder="Ort *" required value={formData.location} onChange={(e) => setFormData(p => ({ ...p, location: e.target.value }))} className="input-luxury w-full" />
              <input type="text" placeholder="PLZ *" required value={formData.zip_code} onChange={(e) => setFormData(p => ({ ...p, zip_code: e.target.value }))} className="input-luxury w-full" />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <input type="number" placeholder="Preis (€)" value={formData.price} onChange={(e) => setFormData(p => ({ ...p, price: e.target.value }))} className="input-luxury w-full" />
              <input type="number" placeholder="Fläche m² *" required value={formData.area_sqm} onChange={(e) => setFormData(p => ({ ...p, area_sqm: e.target.value }))} className="input-luxury w-full" />
              <input type="number" step="0.5" placeholder="Zimmer" value={formData.rooms} onChange={(e) => setFormData(p => ({ ...p, rooms: e.target.value }))} className="input-luxury w-full" />
            </div>
            <textarea placeholder="Beschreibung *" required rows={3} value={formData.description} onChange={(e) => setFormData(p => ({ ...p, description: e.target.value }))} className="input-luxury w-full resize-none" />
            <div className="grid grid-cols-2 gap-4">
              <input type="date" placeholder="Verkauft am" value={formData.sold_date} onChange={(e) => setFormData(p => ({ ...p, sold_date: e.target.value }))} className="input-luxury w-full" />
              <input type="number" placeholder="Tage am Markt" value={formData.days_on_market} onChange={(e) => setFormData(p => ({ ...p, days_on_market: e.target.value }))} className="input-luxury w-full" />
            </div>
            <textarea placeholder="Kundenstimme (optional)" rows={2} value={formData.testimonial} onChange={(e) => setFormData(p => ({ ...p, testimonial: e.target.value }))} className="input-luxury w-full resize-none" />
            <input type="text" placeholder="Autor der Kundenstimme" value={formData.testimonial_author} onChange={(e) => setFormData(p => ({ ...p, testimonial_author: e.target.value }))} className="input-luxury w-full" />
            <input type="text" placeholder="Features (kommagetrennt)" value={featuresInput} onChange={(e) => setFeaturesInput(e.target.value)} className="input-luxury w-full" />
            <textarea placeholder="Bild-URLs (eine pro Zeile)" rows={2} value={imagesInput} onChange={(e) => setImagesInput(e.target.value)} className="input-luxury w-full resize-none" />
            <div className="flex justify-end gap-4 pt-4">
              <button type="button" onClick={() => setDialogOpen(false)} className="btn-secondary">Abbrechen</button>
              <button type="submit" className="btn-primary">Speichern</button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-[#1a2640] border-[#d1af6a]/20">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Referenz löschen?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#c8c6c4]">
              Diese Aktion kann nicht rückgängig gemacht werden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="btn-secondary">Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700 text-white">Löschen</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
